import './sentry';
import './Main.css';
import { Sentry } from './sentry';
import NavBar from './components/NavBar/NavBar';
import CookieConsentBanner from './components/cookie-consent/Banner';
import { appNavigationItems } from './components/NavBar/contentSections';
import { landingPageNavigationItems } from '../landing-page/contentSections';
import { useMemo, useEffect } from 'react';
import { routes } from 'wasp/client/router';
import { Outlet, useLocation } from 'react-router';
import { useAuth } from 'wasp/client/auth';
import { useIsLandingPage } from './hooks/useIsLandingPage';
import { updateCurrentUserLastActiveTimestamp } from 'wasp/client/operations';
import { HelmetProvider } from 'react-helmet-async';
import { LocaleProvider } from './contexts/LocaleContext';

/**
 * use this component to wrap all child components
 * this is useful for templates, themes, and context
 */
export default function App() {
  const location = useLocation();
  const { data: user } = useAuth();
  const isLandingPage = useIsLandingPage();
  const navigationItems = isLandingPage
    ? landingPageNavigationItems
    : appNavigationItems;

  const shouldDisplayAppNavBar = useMemo(() => {
    return (
      location.pathname !== routes.LoginRoute.build() &&
      location.pathname !== routes.SignupRoute.build()
    );
  }, [location]);

  const isAdminDashboard = useMemo(() => {
    const adminRoot = routes.AdminRoute.build();
    return (
      location.pathname === adminRoot ||
      location.pathname.startsWith(`${adminRoot}/`)
    );
  }, [location.pathname]);

  useEffect(() => {
    if (user) {
      const lastSeenAt = new Date(user.lastActiveTimestamp);
      const today = new Date();
      if (today.getTime() - lastSeenAt.getTime() > 5 * 60 * 1000) {
        // Fire-and-forget; .catch surfaces silent failures to Sentry without blocking the UI. (ref: DL-009)
        updateCurrentUserLastActiveTimestamp().catch((e) =>
          Sentry.captureException(e),
        );
      }
    }
  }, [user]);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView();
      }
    }
  }, [location]);

  return (
    <HelmetProvider>
      <LocaleProvider>
        <div className="min-h-screen dark:text-white dark:bg-boxdark-2 overflow-x-clip">
          {isAdminDashboard ? (
            <Outlet />
          ) : (
            <>
              {shouldDisplayAppNavBar && (
                <NavBar navigationItems={navigationItems} />
              )}
              <main className="mx-auto max-w-7xl sm:px-6 lg:px-8">
                <Outlet />
              </main>
            </>
          )}
        </div>
        <CookieConsentBanner />
      </LocaleProvider>
    </HelmetProvider>
  );
}
